import { useMemo } from 'react';

import type { GraphQLCommitment } from '@sola/schema/dotcom/supporters/schema.generated.ts';

type PresentationContext = 'paymentSettings' | 'commitmentDetails';

type GetMakeOneTimePaymentEligibleCommitmentsParams =
  | {
      currentCommitments: GraphQLCommitment[];
      presentationContext: Extract<PresentationContext, 'paymentSettings'>;
      updatedCommitments: GraphQLCommitment[];
      now: Date;
    }
  | {
      presentationContext: Extract<PresentationContext, 'commitmentDetails'>;
      updatedCommitments: GraphQLCommitment[];
      now: Date;
    };

export type MakeOneTimePaymentAlertProps =
  | {
      presentationContext: 'paymentSettings';
      updatedCommitments: GraphQLCommitment[];
      currentCommitments: GraphQLCommitment[];
      now?: Date;
    }
  | {
      presentationContext: 'commitmentDetails';
      updatedCommitments: GraphQLCommitment[];
      now?: Date;
    };

// Active window: the 25th (UTC) through the end of the current month.
// getUTCDate() returns the day of the current month (1-31), so this can
// never leak into the next month — it resets on the 1st.
// Must stay aligned with the backend billing logic, which evaluates in UTC.
const makeOneTimePaymentPromptDay = 25;

const getCommitmentsAfterUpdates = (
  currentCommitments: GraphQLCommitment[],
  updatedCommitments: GraphQLCommitment[],
): GraphQLCommitment[] => {
  const updatedCommitmentsById = new Map(
    updatedCommitments.flatMap((commitment) =>
      commitment.internalId
        ? [[commitment.internalId, commitment] as const]
        : [],
    ),
  );
  const currentCommitmentIds = new Set(
    currentCommitments.flatMap((commitment) =>
      commitment.internalId ? [commitment.internalId] : [],
    ),
  );

  return [
    ...currentCommitments.map((commitment) => {
      return commitment.internalId
        ? (updatedCommitmentsById.get(commitment.internalId) ?? commitment)
        : commitment;
    }),
    ...updatedCommitments.filter(
      (commitment) =>
        !commitment.internalId ||
        !currentCommitmentIds.has(commitment.internalId),
    ),
  ];
};

export const getMakeOneTimePaymentEligibleCommitments = (
  params: GetMakeOneTimePaymentEligibleCommitmentsParams,
): GraphQLCommitment[] => {
  const { updatedCommitments, now } = params;

  // Keep this aligned with the backend billing day, which evaluates the prompt threshold in UTC.
  const isOnOrAfterPromptDayUtc =
    now.getUTCDate() >= makeOneTimePaymentPromptDay;

  if (!isOnOrAfterPromptDayUtc) {
    return [];
  }

  if (params.presentationContext === 'paymentSettings') {
    const commitmentsAfterUpdates = getCommitmentsAfterUpdates(
      params.currentCommitments,
      updatedCommitments,
    );
    const hasAnyMissedPayment = commitmentsAfterUpdates.some(
      (commitment) => commitment.hasMissedPayment === true,
    );

    return hasAnyMissedPayment
      ? []
      : updatedCommitments.filter(
          (commitment) => commitment.hasDueThisMonth === true,
        );
  }

  return updatedCommitments.filter(
    (commitment) =>
      commitment.hasDueThisMonth === true ||
      commitment.hasMissedPayment === true,
  );
};

export const useMakeOneTimePaymentAlert = (
  props: MakeOneTimePaymentAlertProps,
): GraphQLCommitment[] => {
  const { updatedCommitments, now, presentationContext } = props;

  const evaluationDate = useMemo(
    () => now ?? new Date(),
    [now, updatedCommitments],
  );

  const currentCommitments =
    props.presentationContext === 'paymentSettings'
      ? props.currentCommitments
      : undefined;

  return useMemo(() => {
    const byContext = {
      paymentSettings: () =>
        getMakeOneTimePaymentEligibleCommitments({
          presentationContext: 'paymentSettings',
          updatedCommitments,
          currentCommitments: currentCommitments ?? [],
          now: evaluationDate,
        }),
      commitmentDetails: () =>
        getMakeOneTimePaymentEligibleCommitments({
          presentationContext: 'commitmentDetails',
          updatedCommitments,
          now: evaluationDate,
        }),
    } satisfies Record<
      MakeOneTimePaymentAlertProps['presentationContext'],
      () => GraphQLCommitment[]
    >;
    return byContext[presentationContext]();
  }, [
    presentationContext,
    updatedCommitments,
    currentCommitments,
    evaluationDate,
  ]);
};
